import { IJsonQuery } from '@cian/api-models/common/json_query';
import { useDeviceType } from '@cian/ui-kit/responsive';
import cx from 'classnames';
import * as React from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { getTermsValue } from '../../../../../packages/JsonQuery';
import { TModifier, modifyJsonQuery } from '../../../../../packages/Filters';
import { IApplicationState, TThunkDispatch } from '../../../../types/redux';
import { ITagsState } from '../../../../types/filters';
import { TTag } from '../../../../types/tags';
import { changeJsonQuery, IJsonQueryChanged, removeTag, fetchTags } from '../../../../actions/filters';
import { fetchClusters } from '../../../../actions/results';
import { TagsCollapsible } from '../../../../components/TagsCollapsible';
import { Tag } from '../../../../components/Tag';
import * as styles from './Tags.css';

export const Tags: React.FC = () => {
  const deviceType = useDeviceType();
  const dispatch = useDispatch<TThunkDispatch>();
  const jsonQuery = useSelector<IApplicationState, IJsonQuery>(state => state.filters.jsonQuery);
  const tags = useSelector<IApplicationState, ITagsState>(state => state.filters.tags);
  const isSearchResultsOpen = useSelector<IApplicationState, boolean>(state => state.searchResults.isOpen);

  const handleRemove = React.useCallback(
    (tag: TTag) => {
      switch (tag.type) {
        case 'address':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, { action: 'removeGeo', arguments: [{ type: tag.geoType, id: tag.id }] }),
            ),
          );
          break;
        case 'developer':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, { action: 'removeGeo', arguments: [{ type: 'builder', id: tag.id }] }),
            ),
          );
          break;
        case 'distance':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'distance', center: tag.center, radius: tag.radius, name: tag.name }],
              }),
            ),
          );
          break;
        case 'district':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'district', id: tag.id }],
              }),
            ),
          );
          break;
        case 'polygon':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'polygon', coordinates: tag.coordinates, name: tag.name }],
              }),
            ),
          );
          dispatch(fetchTags());
          break;
        case 'railway':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'railway', id: tag.id }],
              }),
            ),
          );
          break;
        case 'region': {
          const currentRegions = getTermsValue('region')(jsonQuery);

          let nextRegions = currentRegions && currentRegions.filter(region => region !== tag.id);
          if (nextRegions && nextRegions.length === 0) {
            nextRegions = null;
          }

          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'setRegion',
                arguments: [nextRegions],
              }),
            ),
          );
          break;
        }

        case 'residentialComplex':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'newobject', id: tag.id }],
              }),
            ),
          );
          break;
        case 'underground':
          dispatch(
            changeJsonQuery(
              applyModifiers(jsonQuery, {
                action: 'removeGeo',
                arguments: [{ type: 'underground', id: tag.id }],
              }),
            ),
          );
          break;
      }

      dispatch(removeTag(tag));
      dispatch(fetchClusters());
    },
    [dispatch, jsonQuery],
  );

  if (deviceType !== 'desktop' || !tags.items) {
    return null;
  }

  return (
    <div className={styles['container']}>
      <div className={styles['wrapper']}>
        <div className={cx(styles['content'], { [styles['content--ectopic']]: isSearchResultsOpen })}>
          <TagsCollapsible>
            {tags.items.map(tag => (
              <Tag key={`tag_${tag.type}_${tag.name}`} onRemove={() => handleRemove(tag)}>
                {tag.name}
              </Tag>
            ))}
          </TagsCollapsible>
        </div>
      </div>
    </div>
  );
};

function applyModifiers(jsonQuery: IJsonQuery, ...modifiers: [TModifier, ...TModifier[]]): IJsonQueryChanged {
  return {
    appliedModifiers: modifiers,
    prevJsonQuery: jsonQuery,
    nextJsonQuery: modifyJsonQuery(jsonQuery, modifiers),
  };
}
